import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconBell, IconCheckAll } from '@/components/icons';
import { LoadingState } from '@/components/QueryState';
import { useMarkNotificationsRead, useNotifications } from '@/data/hooks';
import { NotificationItem } from './NotificationItem';

interface NotificationBellProps {
  /** Full Notificaciones page path for this role group, e.g. "/app/customer/notificaciones". */
  notificationsPath: string;
  /** Gate the query so it only fires while authenticated. */
  enabled?: boolean;
}

/**
 * Top-bar notifications bell: an unread-count badge + a dropdown of the most
 * recent items. Opening the dropdown marks the currently-unread items in view as
 * read (the common "I saw them" behavior); each item also has a per-item "Marcar
 * leída", and a "Marcar todas como leídas" clears everything. Outside-click and
 * Escape close it. Mobile-friendly tap targets; the panel is constrained and
 * scrolls.
 */
export function NotificationBell({ notificationsPath, enabled = true }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch a small recent page for the dropdown + the exact unread count.
  const query = useNotifications({ limit: 12 }, enabled);
  const markRead = useMarkNotificationsRead();

  const items = query.data?.items ?? [];
  const unreadCount = query.data?.unreadCount ?? 0;

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleOpen() {
    const next = !open;
    setOpen(next);
    // On open, mark the unread items currently shown as read.
    if (next) {
      const unreadIds = items.filter((n) => !n.readAt).map((n) => n.notificationId);
      if (unreadIds.length > 0) markRead.mutate({ notificationIds: unreadIds });
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleOpen}
        aria-label={unreadCount > 0 ? `Notificaciones (${unreadCount} sin leer)` : 'Notificaciones'}
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-text-primary hover:bg-surface-muted"
      >
        <IconBell />
        {unreadCount > 0 ? (
          <span className="absolute right-1 top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-4 text-on-danger">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 flex max-h-[70vh] w-80 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <p className="text-sm font-semibold text-text-primary">Notificaciones</p>
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={() => markRead.mutate({ all: true })}
                disabled={markRead.isPending}
                className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline disabled:opacity-60"
              >
                <IconCheckAll className="h-3.5 w-3.5" />
                Marcar todas
              </button>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {query.isLoading ? (
              <LoadingState label="Cargando…" />
            ) : items.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-text-muted">
                No tienes notificaciones.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => (
                  <li key={n.notificationId}>
                    <NotificationItem
                      notification={n}
                      compact
                      onMarkRead={(id) => markRead.mutate({ notificationIds: [id] })}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-border px-4 py-2.5">
            <Link
              to={notificationsPath}
              onClick={() => setOpen(false)}
              className="block text-center text-sm font-medium text-brand-primary hover:underline"
            >
              Ver todas
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
